import React from "react";
import PropTypes from "prop-types";
import Script from "next/script";
import { useInterval } from "@gonoodle/gn-universe-utils";

const missingOneTrustProvider =
  "You forgot to wrap your app in <OneTrustProvider>";
const OneTrustContext = React.createContext({
  get isReady() {
    throw new Error(missingOneTrustProvider);
  },
});
OneTrustContext.displayName = "OneTrustContext";

export const PERFORMANCE_SDKS = "C0002";
export const CONSENT_NOT_COLLECTED = -1;
export const CONSENT_NOT_GIVEN = 0;
export const CONSENT_GIVEN = 1;

const getConsentStatus = (categoryId) => {
  if (window.OnetrustActiveGroups.includes(categoryId)) {
    return CONSENT_GIVEN;
  }
  return CONSENT_NOT_GIVEN;
};

export function Provider({ children }) {
  const [isReady, setIsReady] = React.useState(false);

  /**
   * keep waiting till OneTrust is ready.
   * This is necessary because even when the oneTrust loads it still fetches other scripts.
   * and OneTrust will not be available until those scripts are available.
   */
  useInterval(
    () => {
      if (
        window.OneTrust &&
        window.OnetrustActiveGroups &&
        window.OnetrustActiveGroups.length > 2 // Active groups will be more than two when OneTrust is ready.
      ) {
        setIsReady(true);
      }
    },
    isReady ? null : 2000,
  );

  return (
    <OneTrustContext.Provider value={{ isReady }}>
      {children}
      <Script
        src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
        data-domain-script={process.env.NEXT_PUBLIC_ONETRUST_SCRIPT_KEY}
        strategy="lazyOnload"
      />
    </OneTrustContext.Provider>
  );
}

export function useSDKStatus() {
  const { isReady } = React.useContext(OneTrustContext);
  return {
    isReady,
  };
}

export function useConsentStatus(categoryId) {
  const [consentStatus, setConsentStatus] = React.useState(
    CONSENT_NOT_COLLECTED,
  );
  const { isReady } = useSDKStatus();
  React.useEffect(() => {
    let isCurrent = true;
    if (isReady) {
      setConsentStatus(getConsentStatus(categoryId));

      window.OptanonWrapper = () => {
        if (isCurrent) {
          setConsentStatus(getConsentStatus(categoryId));
        }
      };
    }

    return () => {
      isCurrent = false;
    };
  }, [categoryId, isReady]);

  return consentStatus;
}

export function showPreferenceCenter() {
  if (window.OneTrust && window.OnetrustActiveGroups) {
    window.OneTrust.ToggleInfoDisplay();
  }
}

Provider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
